export interface ISrcSetImages {
  name: string
  url: string
  width: number
  height: number
}

interface ISrcSet {
  key: string
  width: number
}

export const srcSetMappings = [
  // Photo-specific
  { key: 'w100', width: 100 },
  { key: 'w200', width: 200 },
  { key: 'w300', width: 300 },
  { key: 'w400', width: 400 },
  { key: 'w500', width: 500 },
  { key: 'w600', width: 600 },
  { key: 'w700', width: 700 },
  { key: 'w800', width: 800 },
  { key: 'w900', width: 900 },
  { key: 'w1000', width: 1000 },
  { key: 'w1200', width: 1200 },
  { key: 'w1400', width: 1400 },
  { key: 'w1600', width: 1600 }
]

export const selectImage = (requestedImage: string, urls: ISrcSetImages[]) =>
  urls.find(url => url.name === requestedImage)

export const buildImageSrcSet = (
  srcSetMappings: ISrcSet[],
  imgUrls: ISrcSetImages[]
) => {
  return srcSetMappings
    .filter(image => {
      const img = selectImage(image.key, imgUrls)
      return typeof img != 'undefined'
    })
    .map(image => {
      const img = selectImage(image.key, imgUrls)
      if (img) {
        return `${img.url}${img.width ? ` ${img.width}w` : ''}`
      } else {
        return ''
      }
    })
    .join(', ')
}

export const mediaQuery = (
  minWidth?: number | null,
  maxWidth?: number | null
) => {
  if (minWidth && maxWidth) {
    return `(min-width: ${minWidth}px) and (max-width: ${maxWidth}px)`
  } else if (maxWidth && !minWidth) {
    return `(max-width: ${maxWidth}px)`
  } else if (minWidth && !maxWidth) {
    return `(min-width: ${minWidth}px)`
  } else {
    return ''
  }
}

const gridImageSize = (
  minScreenWidth: number | null,
  maxScreenWidth: number | null,
  imagesPerGridRow: number,
  aspectRatio: number,
  gridGutterWidthInVW = 1,
  paddingWidthInVW = 2
) => {
  const totalGutterWidth = (imagesPerGridRow - 1) * gridGutterWidthInVW
  const totalPictureWidthInARow = 100 - totalGutterWidth - 2 * paddingWidthInVW
  const defaultImgWidth = totalPictureWidthInARow / imagesPerGridRow

  const imageWidth = Math.ceil(defaultImgWidth * aspectRatio)
  const cappedWidth = Math.min(imageWidth, totalPictureWidthInARow)

  const query = mediaQuery(minScreenWidth, maxScreenWidth)

  return [
    `${query} and (min-resolution: 144dpi) ${Math.ceil(cappedWidth * 0.75)}vw`,
    `${query} ${cappedWidth}vw`
  ].join(', ')
}

export const getSrcSetData = (
  imgUrls: ISrcSetImages[],
  defaultWidthKey = 'w500',
  aspectRatio = 1
) => {
  if (!imgUrls) return null

  const sizes = getPhotoDuneImageSizes(aspectRatio)

  return {
    defaultImage: selectImage(defaultWidthKey, imgUrls),
    srcSet: buildImageSrcSet(srcSetMappings, imgUrls),
    sizes
  }
}

// These sizes are generated in accordance with breakpoints defined in grid.js
// and used by gridStyles.js
export const getPhotoDuneImageSizes = (aspectRatio: number) =>
  [
    gridImageSize(null, 719, 1, 1),
    gridImageSize(720, 999, 2, aspectRatio),
    gridImageSize(1000, 1199, 3, aspectRatio),
    gridImageSize(1200, 1919, 4, aspectRatio),
    gridImageSize(1920, null, 5, aspectRatio)
  ].join(', ')

export const getPreviewSrcSetData = (
  imgUrls: ISrcSetImages[],
  defaultWidthKey = 'w700',
  aspectRatio = 1
) => {
  if (!imgUrls) return null

  const imgWidth = aspectRatio < 1 ? aspectRatio * 50 : 50

  const sizes = [
    `${mediaQuery(null, 719)} and (min-resolution: 144dpi) 75vw`,
    `${mediaQuery(null, 719)} 100vw`,
    `${mediaQuery(720, 1400)} ${imgWidth}vw`,
    `${mediaQuery(1401, null)} 700px`
  ].join(', ')

  return {
    defaultImage: selectImage(defaultWidthKey, imgUrls),
    srcSet: buildImageSrcSet(srcSetMappings, imgUrls),
    sizes
  }
}
